// ** React Imports
import { useEffect, useState, useMemo } from "react";

// ** MUI Imports
import {
  IconButton,
  Box,
  Typography,
  MenuItem,
  FormControlLabel,  
  Checkbox,  
  InputAdornment,  
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
} from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import CustomTextField from "../../../components/mui/text-field";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { useTheme } from "@mui/material/styles";
import Icon from "../../../components/icon";
import CustomAvatar from "../../../components/mui/avatar";
import CustomChip from "../../../components/mui/chip";
import AddEmployeeDoodle from "images/AddEmployeeDoodle.png";
import { GetNotes, SaveNote, DeleteNote } from "../logic/NotesLogic";
import { convertESTToLocal, convertESTToLocalDateTime, convertToEST } from "components/formatDate";


const NotesComponent = ({customerID, requestID, requestType, setLoading, rules, module="detail"}) => {

  //#region States
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editNoteID, setEditNoteID] = useState(0);
  const [noteBtnLoader, setNoteBtnLoader] = useState(false);
  const [showMoreDisabled, setShowMoreDisabled] = useState(false);
  const [searchNoteText, setSearchNoteText] = useState('');
  const [notesSort, setNotesSort] = useState(true);
  const [showInputField, setShowInputField] = useState(false);
  const [displayBanner, setDisplayBanner] = useState(true);
  const [notesPagination, setNotesPagination] = useState({
    pageNo: 1,
    recordsPerPage: 100,
  });

  const TAG_MAP = {
    'Request Note': 'Request',
    'Assessment Note': 'Assessment',
    'Estimate Note': 'Estimate',
    'Work Order Note': 'WorkOrder',
    'WorkOrder Note': 'WorkOrder',
    'Invoice Note': 'Invoice',
    'Customer Note': 'Customer',
  };
  
  // Use static or derive dynamically from notes
  const ALL_TYPES = ['Request','Assessment','Estimate','WorkOrder','Invoice','Customer'];
  
  const mapTagToType = (tag) => TAG_MAP[tag?.trim()] || null;
  const [selectedTypes, setSelectedTypes] = useState([]);

  const MySwal = withReactContent(Swal);
  const theme = useTheme();
  //#endregion States

  //#region Handlers
  const handleNoteChange = (val) => {
    setNewNote(val);
  }
  const fetchNotes = async (notesPagination) => {
    setLoading(true);
    try {

      const obj = {
        entityID: requestID,
        entityType: "Request",
        pageNo: notesPagination.pageNo,
        recordsPerPage: notesPagination.recordsPerPage,
      }

      const fetchedNotes = await GetNotes(obj);
      // console.log(fetchedNotes);
      //console.log(fetchedNotes.result.length === fetchedNotes.totalRecords)
      setShowMoreDisabled(fetchedNotes.result.length === fetchedNotes.totalRecords)  
      setNotes(fetchedNotes.result);

    } catch(error){
      console.log(error);
    } finally {
      setLoading(false);
    }
}

useEffect(() => {
  if (requestID) {
    fetchNotes(notesPagination);
  }
}, [requestID, customerID]);

const handleSaveNote = async () => {
  const note = {
    noteID: editNoteID ? editNoteID : 0,
    description: newNote,
    entityID: requestID,
    entityType: "Request",
    tagName: `${requestType} Note`
  }

  const saveNote = await SaveNote(note);
  
  fetchNotes(notesPagination);    
  
  setNewNote('');
  setEditNoteID(0);
  setNoteBtnLoader(false);
  setShowInputField(false);
}

const handleEditNote = async (noteID, noteDescription) => {
  setNoteBtnLoader(true);
  setNewNote(noteDescription);
  setEditNoteID(noteID);
  setShowInputField(true);
}

const handleDeleteNote = async (noteID) => {
  try {
    setNoteBtnLoader(true);

    const result = await MySwal.fire({
      title: '<strong>Are you sure?</strong>',
      html: `<span>Are you sure you want to delete note?</span>`,
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it',
      width: '390px',
      padding: '1rem',
      focusConfirm: false,
      showCloseButton: true,
      closeButtonHtml: '&times;',
      customClass: {
        popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
        title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
        htmlContainer: 'custom-swal-html',
        confirmButton: 'custom-swal-confirm',
        cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
        closeButton: 'custom-swal-close'
      }
    });

    if (result.isConfirmed) {
      const deleteResult = await DeleteNote(noteID, requestID, "Request");
      
      if (deleteResult && !deleteResult.isError) {
        fetchNotes(notesPagination);
        setNoteBtnLoader(false);
      } else {
        setNoteBtnLoader(false);
      }
    }
  } catch (error) {
    console.error('Error deleting Note:', error);
    setLoading(false);
    setNoteBtnLoader(false);
  } finally {
    setLoading(false);
    setNoteBtnLoader(false);
  }
}

const handleShowMoreNotes = () => {
  setNotesPagination((prev) => {
    const updatedPagination = {
      ...prev,
      recordsPerPage: prev.recordsPerPage + 100,
    };
    // Fetch notes with updated pagination
    fetchNotes(updatedPagination);
    return updatedPagination;
  });
};

const displayedNotes = useMemo(() => {
  const q = searchNoteText.trim().toLowerCase();

  const filtered = notes.filter(n => {
    const matchesSearch =
      !q ||
      n.description?.toLowerCase().includes(q) ||
      n.createdBy?.toLowerCase().includes(q);

    const type = mapTagToType(n.tagName);
    const matchesType =
      selectedTypes.length === 0 ? true : selectedTypes.includes(type);

    return matchesSearch && matchesType;
  });

  filtered.sort((a, b) => {
    const da = new Date(a.createDate);
    const db = new Date(b.createDate);
    return notesSort ? db - da : da - db; // Newest first when notesSort === true
  });

  return filtered;
}, [notes, searchNoteText, selectedTypes, notesSort]);

//#endregion Handlers

  return (
      <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      {/* Full Width Banner at the top */}
      {displayBanner && (
        <Box 
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'rgb(35, 162, 109, 0.1)',
            fontSize: '15px',
            borderRadius: 1,       
            p: 2,
            width: '100%',
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1,ml: 3 }}>
            <Icon icon="tabler:info-circle" color="success" />
            <Typography variant="h6" color="text.primary">
              These are internal Notes that will not be seen by the Customer
            </Typography>
          </Box>
          <Tooltip title='Remove'>
            <IconButton 
              size="small" 
              onClick={() => setDisplayBanner(false)}
            >
              <Icon icon='tabler:x' fontSize="1rem" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Header Section - Always at top */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body1" fontWeight="bold">
            {notesSort ? "Newest First" : "Oldest First"}
          </Typography>
          <IconButton onClick={() => setNotesSort(!notesSort)}>
            <Icon
              icon={
                notesSort ? "tabler:sort-descending" : "tabler:sort-ascending"
              }
            />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            width: { xs: "100%", md: "auto" },
          }}
        >
          <CustomTextField
            placeholder="Search Notes"
            value={searchNoteText}
            onChange={(e) => setSearchNoteText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="tabler:search" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchNoteText && (
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setSearchNoteText("");
                      }}
                      aria-label="clear"
                      sx={{ mr: 2 }}
                    >
                      <Icon icon="tabler:x" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: "220px" },
            }}
          />
          {module !== "creation" && (
            <CustomTextField
              select
              fullWidth
              value={selectedTypes}
              size="small"
              onChange={(e) => {
                let v = e.target.value;
                const arr = typeof v === "string" ? v.split(",") : v;
                if (arr.includes("all")) {
                  setSelectedTypes(
                    selectedTypes.length === ALL_TYPES.length ? [] : ALL_TYPES
                  );
                } else {
                  setSelectedTypes(arr);
                }
              }}
              sx={{
                width: { xs: "100%", sm: "220px" },
              }}
              // disabled={!hasCommunicationGetPermission}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => {
                  const vals =
                    typeof selected === "string"
                      ? selected.split(",")
                      : selected;
                  return vals.length > 0
                    ? `${vals.length} type(s) selected`
                    : "Note Type";
                },
                MenuProps: {
                  PaperProps: {
                    style: { maxHeight: 6 * 48 + 8, width: 240 },
                  },
                },
              }}
            >
              <MenuItem value="all">
                <Checkbox
                  checked={selectedTypes.length === ALL_TYPES.length}
                />
                Show All
              </MenuItem>

              <MenuItem value="Request">
                <Checkbox checked={selectedTypes.includes("Request")} />
                Requests
              </MenuItem>
              <MenuItem value="Assessment">
                <Checkbox checked={selectedTypes.includes("Assessment")} />
                Assessments
              </MenuItem>
              <MenuItem value="Estimate">
                <Checkbox checked={selectedTypes.includes("Estimate")} />
                Estimates
              </MenuItem>
              <MenuItem value="WorkOrder">
                <Checkbox checked={selectedTypes.includes("WorkOrder")} />
                Work Orders
              </MenuItem>
              <MenuItem value="Invoice">
                <Checkbox checked={selectedTypes.includes("Invoice")} />
                Invoices
              </MenuItem>
              <MenuItem value="Customer">
                <Checkbox checked={selectedTypes.includes("Customer")} />
                Customer
              </MenuItem>
            </CustomTextField>
          )}
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* When there are notes */}
        {notes.length > 0 && (
          <Box
            sx={{
              overflowY: "auto",
              flexGrow: 1,
            }}
          >
            <Box sx={{ mb: 4 }}>
              {displayedNotes.length > 0 ? (
                displayedNotes.map((note) => (
                  <Box
                    key={note.noteID}
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 4,
                      flexDirection: { xs: "column", md: "row" },
                      alignItems: { xs: "center", md: "flex-start" },
                      textAlign: { xs: "center", md: "left" },
                    }}
                  >
                    {/* Avatar Section */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: { xs: "center", md: "flex-start" },
                      }}
                    >
                      <Tooltip title={note.createdBy}>
                        <CustomAvatar skin="dark" color="warning">
                          {note.createdBy
                            .split(" ")
                            .map((name) => name[0])
                            .join("")}
                        </CustomAvatar>
                      </Tooltip>
                    </Box>

                    {/* Note Details Section */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        alignItems: { xs: "center", md: "flex-start" },
                        width: { xs: "100%", md: "auto" },
                      }}
                    >
                      {/* Header Section */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexDirection: { xs: "column", md: "row" },
                          textAlign: { xs: "center", md: "left" },
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold">
                          {note.createdBy}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {convertESTToLocalDateTime(note.createDate)}
                        </Typography>
                        <CustomChip
                          label={note && note.tagName ? note.tagName : ""}
                          skin="light"
                          color="success"
                        />
                      </Box>

                      {/* Description Section */}
                      <Typography
                        variant="body1"
                        sx={{ textAlign: { xs: "center", md: "left" } }}
                      >
                        {note.description}{" "}
                        {note.modifiedBy !== null && (
                          <span style={{ fontSize: "10px" }}>
                            {" "}
                            (Edited)
                          </span>
                        )}
                      </Typography>

                      {/* Actions Section */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          justifyContent: {
                            xs: "center",
                            md: "flex-start",
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle"
                          color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                          sx={{
                            cursor: noteBtnLoader
                              ? "not-allowed"
                              : "pointer",
                            "&:hover": {
                              color: "primary.main",
                              textDecoration: "underline",
                            },
                            opacity: noteBtnLoader ? 0.5 : 1,
                          }}
                          onClick={() => {
                            if (!noteBtnLoader)
                              handleEditNote(note.noteID, note.description);
                          }}
                        >
                          Edit
                        </Typography>
                        <Typography variant="subtitle">•</Typography>
                        <Typography
                          variant="subtitle"
                          color="primary"
                          sx={{
                            cursor: noteBtnLoader
                              ? "not-allowed"
                              : "pointer",
                            "&:hover": {
                              color: "primary.main",
                              textDecoration: "underline",
                            },
                            opacity: noteBtnLoader ? 0.5 : 1,
                          }}
                          onClick={() => {
                            if (!noteBtnLoader)
                              handleDeleteNote(note.noteID);
                          }}
                        >
                          Delete
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body1">
                    No matching result found
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Show More Button */}
            {searchNoteText === "" && !showMoreDisabled && displayedNotes.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <CustomChip
                  skin="light"
                  sx={{ lineHeight: 1, "&:hover": { color: "#fff" } }}
                  color="info"
                  label="Show More"
                  onClick={handleShowMoreNotes}
                />
              </Box>
            )}
          </Box>
        )}

        {/* When there are no notes at all - Centered Card */}
        {notes.length === 0 && !showInputField && (
           <Box
  sx={{
    display: "flex",
    justifyContent: "center",
    mx: "auto",
    border: "1px solid #EAEAEA",
    borderRadius: 1,
    width: "100%",
    height: 280,
    py: 4,
    alignItems: "center" ,
  }}
>
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2,
      justifyContent: "center",
      alignItems: "center",
      width: { xs: "90%", sm: "100%" }, 
      maxWidth: 400, 
      px: 2,
      textAlign: "center" 
    }}
  >
                <img
                  src={AddEmployeeDoodle}
                  alt="Empty State"
                  style={{ width: "100px", height: "auto" }}
                />
                <Typography variant="h6">No Notes added yet</Typography>
                 <Button
                  variant="outlined"
                  color="main"
                  startIcon={<Icon icon="tabler:plus" />}
                  onClick={() => setShowInputField(true)}
                  sx={{ 
                    minWidth: 150,
                    mt: 2
                  }}
                >
                  Add Note
                </Button>
              </Box>
            </Box>
               
        )}
      </Box>

      {/* Input Field Section - Always at the bottom */}
      {(notes.length > 0 || showInputField) && (
        <Box
          sx={{
            mt: "auto",
            pt: 3,
            borderTop: notes.length > 0 ? `1px solid ${theme.palette.divider}` : 'none'
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 0 },
            }}
          >
            <CustomTextField
              fullWidth
              placeholder="Add your note here"
              value={newNote}
              onChange={(e) => handleNoteChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSaveNote();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSaveNote}>
                      <Icon icon="gg:add" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: { xs: "100%", md: "50%" },
              }}
            />
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                px: 4,
                py: 2,
                borderRadius: 1,
                justifyContent: "flex-end",
              }}
            >
              <Typography variant="h5" color="primary">
                Total Notes: {notes.length}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default NotesComponent;